import { formatDate } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2,
    Self,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { App } from '@df/core/app';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ICalendarOptions, ICalendarValidateDay } from '@df/ui/form/calendar.component.def';
import { FormFieldAbstract } from '@df/ui/form/form-field.abstract';
import { NUi } from '../df-ui.config';

export interface IDatepickerOptions extends ICalendarOptions {}

@Directive()
export abstract class DatepickerComponentDef extends FormFieldAbstract implements AfterViewInit, OnInit, OnDestroy, ControlValueAccessor {
    private _isOpened?: boolean;
    private _listenerInputClickFn?: () => void;
    private _listenerInputFocusFn?: () => void;
    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    opened$ = new BehaviorSubject(false);

    protected _inputFieldValue?: string = undefined;
    get inputFieldValue(): string | undefined {
        return this._inputFieldValue;
    }

    set inputFieldValue(value: string) {
        this.setInputFieldValue(value);
    }

    @ViewChild('input')
    inputElement?: ElementRef<HTMLInputElement>;

    /**
     * Datepicker options
     * for now just calendar options and passed there
     * @type {IDatepickerOptions}
     */
    @Input()
    options?: IDatepickerOptions;

    @Input()
    dateFormat: string = this.uiConfig.date.format;

    /**
     * Value change output
     */
    @Output()
    valueChange = new EventEmitter<any>();

    @Output()
    validateDay = new EventEmitter<ICalendarValidateDay>();

    constructor(
        protected renderer: Renderer2,
        @Inject(NUi.OPTIONS) private uiConfig: NUi.IModuleOptions,
        app: App,
        @Optional() @Self() ngControl: NgControl
    ) {
        super(app, ngControl);
    }

    ngOnInit() {
        this.opened$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(to => this.onOpened(to));
    }

    ngAfterViewInit() {
        if (this.inputElement) {
            this._listenerInputClickFn = this.renderer.listen(this.inputElement.nativeElement, 'click', () => this.open());
            this._listenerInputFocusFn = this.renderer.listen(this.inputElement.nativeElement, 'focus', () => this.open());
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        if (this._listenerInputClickFn) {
            this._listenerInputClickFn();
        }

        if (this._listenerInputFocusFn) {
            this._listenerInputFocusFn();
        }
    }

    protected setInputFieldValue(value: string) {
        this._inputFieldValue = value;
    }

    onValueChange(value: any) {
        this.value = value;

        this.onChange(this.value);

        this.updateInputfield();

        this.valueChange.next(this.value);

        this.close();
    }

    override writeValue(value: any): void {
        super.writeValue(value);

        this.updateInputfield();
    }

    get isOpen() {
        return this._isOpened;
    }

    onOpened(to: boolean) {
        this._isOpened = to;
    }

    open() {
        this.opened$.next(true);
    }

    close() {
        this.opened$.next(false);
    }

    onValidate(event: ICalendarValidateDay) {
        this.validateDay.emit(event);
    }

    updateInputfield() {
        let formattedValue: string | null = '';

        if (this.value) {
            formattedValue = this.formatDateTime(this.value);
        }

        this.inputFieldValue = formattedValue || '';

        if (this.inputElement && this.inputElement.nativeElement) {
            this.inputElement.nativeElement.value = this.inputFieldValue || '';
            const event = new Event('change');
            this.inputElement.nativeElement.dispatchEvent(event);
        }
    }

    formatDateTime(date: string) {
        let formattedValue: string | null = null;

        if (date) {
            formattedValue = this.formatDate(date, this.dateFormat);
        }

        return formattedValue;
    }

    formatDate(date: string | number | Date, format: string) {
        if (!date) {
            return '';
        }

        return formatDate(date, format, this.app.data.locale);
    }
}
